import {
  ApiResponse,
  FetchOptions,
  ApiErrorResponse,
  ApiSuccessResponse,
} from "../../types/api";
import { API_CONFIG } from "./config";
import { ServerTokenManager } from "./token-manager-server";

const createServerHeaders = async (options: FetchOptions = {}) => {
  const token = ServerTokenManager.getToken();

  return {
    ...API_CONFIG.DEFAULT_HEADERS,
    // ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };
};

const handleServerResponse = async <T extends object>(
  response: Response,
  options: FetchOptions = {},
): Promise<ApiResponse<T>> => {
  let data: any;

  try {
    const text = await response.text();
    data = text ? JSON.parse(text) : null;
  } catch (parseError) {
    console.warn("Failed to parse response JSON:", parseError);
    data = null;
  }

  const authHeader = response.headers.get("Authorization");
  // if (authHeader && authHeader.startsWith("Bearer ")) {
  //   const token = authHeader.replace("Bearer ", "");
  //   ServerTokenManager.setToken(token);
  // }

  if (!response.ok) {
    const errorResponse: ApiErrorResponse = data || {
      error: {
        message: "An unknown error occurred",
        error: "unknown_error",
        statusCode: response.status,
      },
      meta: {
        success: false,
        timestamp: new Date().toISOString(),
      },
    };

    const isIgnoredStatus =
      options.ignoreStatusCodes?.includes(response.status) ?? false;

    if (!isIgnoredStatus) {
      console.error("Server API Error:", {
        url: response.url,
        status: response.status,
        statusText: response.statusText,
        error: errorResponse.error,
      });

      // Send to Sentry, especially for 500 errors
      if (process.env.NEXT_PUBLIC_ENABLE_SENTRY?.toLowerCase() === "true") {
        const Sentry = await import("@sentry/nextjs");
        Sentry.captureException(
          new Error(
            `Server API Error: ${response.status} ${response.statusText} - ${errorResponse.error.message}`,
          ),
          {
            tags: {
              errorType: "server_api_error",
              statusCode: response.status,
              url: response.url,
            },
            extra: {
              url: response.url,
              status: response.status,
              statusText: response.statusText,
              error: errorResponse.error,
            },
            level: response.status >= 500 ? "error" : "warning",
          },
        );
      }
    }

    return errorResponse;
  }

  return (data || {
    data: {} as T,
    meta: {
      success: true,
      timestamp: new Date().toISOString(),
    },
    statusCode: response.status,
  }) as ApiSuccessResponse<T>;
};

export const serverApi = {
  async get<T extends object>(
    path: string,
    options: FetchOptions = {},
  ): Promise<ApiResponse<T>> {
    try {
      const headers = await createServerHeaders(options);
      const response = await fetch(`${API_CONFIG.SERVER_BASE_URL}${path}`, {
        method: "GET",
        headers,
        cache: options.cache || "no-store",
        ...(options.revalidate !== undefined && {
          next: { revalidate: options.revalidate },
        }),
        ...(options.tags && { next: { tags: options.tags } }),
      });

      return handleServerResponse<T>(response, options);
    } catch (error) {
      console.error(
        `Server API Error for GET ${API_CONFIG.SERVER_BASE_URL}${path}:`,
        error,
      );

      // Send network errors to Sentry
      if (process.env.NEXT_PUBLIC_ENABLE_SENTRY?.toLowerCase() === "true") {
        const Sentry = await import("@sentry/nextjs");
        Sentry.captureException(error, {
          tags: {
            errorType: "server_api_network_error",
            method: "GET",
            path: path,
          },
          extra: {
            url: `${API_CONFIG.SERVER_BASE_URL}${path}`,
            method: "GET",
          },
          level: "error",
        });
      }

      return {
        error: {
          message: "Network error occurred",
          error: "network_error",
          statusCode: 500,
        },
        meta: {
          success: false,
          timestamp: new Date().toISOString(),
        },
      } as ApiErrorResponse;
    }
  },

  async post<T extends object>(
    path: string,
    body?: any,
    options: FetchOptions = {},
  ): Promise<ApiResponse<T>> {
    try {
      const headers = await createServerHeaders(options);
      const response = await fetch(`${API_CONFIG.SERVER_BASE_URL}${path}`, {
        method: "POST",
        headers,
        body: body ? JSON.stringify(body) : undefined,
        cache: options.cache || "no-store",
      });

      return handleServerResponse<T>(response, options);
    } catch (error) {
      console.error(
        `Server API Error for POST ${API_CONFIG.SERVER_BASE_URL}${path}:`,
        error,
      );

      // Send network errors to Sentry
      if (process.env.NEXT_PUBLIC_ENABLE_SENTRY?.toLowerCase() === "true") {
        const Sentry = await import("@sentry/nextjs");
        Sentry.captureException(error, {
          tags: {
            errorType: "server_api_network_error",
            method: "POST",
            path: path,
          },
          extra: {
            url: `${API_CONFIG.SERVER_BASE_URL}${path}`,
            method: "POST",
            body: body,
          },
          level: "error",
        });
      }

      return {
        error: {
          message: "Network error occurred",
          error: "network_error",
          statusCode: 500,
        },
        meta: {
          success: false,
          timestamp: new Date().toISOString(),
        },
      } as ApiErrorResponse;
    }
  },

  async put<T extends object>(
    path: string,
    body?: any,
    options: FetchOptions = {},
  ): Promise<ApiResponse<T>> {
    try {
      const headers = await createServerHeaders(options);
      const response = await fetch(`${API_CONFIG.SERVER_BASE_URL}${path}`, {
        method: "PUT",
        headers,
        body: body ? JSON.stringify(body) : undefined,
        cache: options.cache || "no-store",
      });

      return handleServerResponse<T>(response, options);
    } catch (error) {
      console.error(
        `Server API Error for PUT ${API_CONFIG.SERVER_BASE_URL}${path}:`,
        error,
      );

      // Send network errors to Sentry
      if (process.env.NEXT_PUBLIC_ENABLE_SENTRY?.toLowerCase() === "true") {
        const Sentry = await import("@sentry/nextjs");
        Sentry.captureException(error, {
          tags: {
            errorType: "server_api_network_error",
            method: "PUT",
            path: path,
          },
          extra: {
            url: `${API_CONFIG.SERVER_BASE_URL}${path}`,
            method: "PUT",
            body: body,
          },
          level: "error",
        });
      }

      return {
        error: {
          message: "Network error occurred",
          error: "network_error",
          statusCode: 500,
        },
        meta: {
          success: false,
          timestamp: new Date().toISOString(),
        },
      } as ApiErrorResponse;
    }
  },

  async delete<T extends object>(
    path: string,
    options: FetchOptions = {},
  ): Promise<ApiResponse<T>> {
    try {
      const headers = await createServerHeaders(options);
      const response = await fetch(`${API_CONFIG.SERVER_BASE_URL}${path}`, {
        method: "DELETE",
        headers,
        cache: options.cache || "no-store",
      });

      return handleServerResponse<T>(response, options);
    } catch (error) {
      console.error(
        `Server API Error for DELETE ${API_CONFIG.SERVER_BASE_URL}${path}:`,
        error,
      );

      // Send network errors to Sentry
      if (process.env.NEXT_PUBLIC_ENABLE_SENTRY?.toLowerCase() === "true") {
        const Sentry = await import("@sentry/nextjs");
        Sentry.captureException(error, {
          tags: {
            errorType: "server_api_network_error",
            method: "DELETE",
            path: path,
          },
          extra: {
            url: `${API_CONFIG.SERVER_BASE_URL}${path}`,
            method: "DELETE",
          },
          level: "error",
        });
      }

      return {
        error: {
          message: "Network error occurred",
          error: "network_error",
          statusCode: 500,
        },
        meta: {
          success: false,
          timestamp: new Date().toISOString(),
        },
      } as ApiErrorResponse;
    }
  },
};
